import { FC, useCallback, useEffect, useState } from 'react'
import {
    Alert,
    Button,
    ColumnLayout,
    Header
} from '@cloudscape-design/components'
import FormInput from '../Input'
import { useForm } from 'react-hook-form'
import FormSelect from '../Select'
import { useEditTagsManagementFormStore } from '../../../stores/tags/editTagsManagementFormStore'
import useSingleTags from '../../../hooks/query/useSingleTags'
import LoadingSpinner from '../../LoadingSpinner'
 
import CONSTANT from '../../../constants'
 
import { UpdateTags } from '../../../types/tags'
import useUpdateTags from '../../../hooks/mutate/useUpdateTags'
import useAccounts from '../../../hooks/query/useAccounts'
import ApproverOverview from '../ApproverOverview'
import { useOktaAuth } from '@okta/okta-react'
 
const { DATA_CLASSIFICATION_LIST, COST_TYPE_LIST, PLATFORM_LIST } = CONSTANT
 
interface EditGenericTagsProps {
    tagsId: string
    layout_col?: number
    page_context?: string
}
 
const EditGenericTags: FC<EditGenericTagsProps> = ({
    tagsId,
    layout_col = 2
}) => {
    const { authState } = useOktaAuth()
    const thisUserEmail = authState?.idToken?.claims.email as string
 
    const { data: originalTags, isLoading: isLoadingTags } = useSingleTags(tagsId)
 
    const {
        formValues,
        actions: { setFormValues, setFormValidity, setOriginalRequestFormValues }
    } = useEditTagsManagementFormStore()
 
    const { mutate: updateTagsById, isLoading: isLoadingUpdateTags } =
        useUpdateTags(tagsId)

    // Fetch all accounts for duplicate systemAlias validation
    const { data: accounts } = useAccounts()

    // Validate that systemAlias is not already used as platformSubgroupName in another approved account
    const validateSystemAlias = useCallback(
        (value: string): boolean | string => {
            if (!value || !accounts) return true
            const normalized = value.trim().toLowerCase()

            // Resolve the type of the account currently being edited (via tagsID match)
            const currentAccount = accounts.find((account) => account.tagsID === tagsId)
            const currentType = currentAccount?.type

            const existingAccount = accounts.find((account) => {
                // Exclude accounts linked to the same tags record
                if (account.tagsID === tagsId) return false
                const existing = account.approval?.platformSubgroupName
                return existing?.trim().toLowerCase() === normalized
            })

            if (existingAccount && existingAccount.type === currentType) {
                return 'This System Alias is already in use as a Platform Subgroup Name in another approved account request.'
            }
            return true
        },
        [accounts, tagsId]
    )
 
    const {
        control,
        reset,
        setValue,
        formState: { isValid, dirtyFields }
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: formValues.details,
        shouldUseNativeValidation: true
    })
 
    useEffect(() => {
        if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'opex') {
            setValue('WBSCode', '')
            setFormValues('WBSCode', '')
        } else if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'capex') {
            setValue('costCenter', '')
            setFormValues('costCenter', '')
        }
    }, [formValues.fundingSourceType, setValue, setFormValues])
 
    useEffect(() => {
        setFormValidity(isValid)
    }, [isValid, setFormValidity])
 
    const [notification, setNotification] = useState<any>({
        type: '',
        statusIconAriaLabel: '',
        msg: ''
    })
    const [showTagsNotification, setShowTagsNotification] = useState(false)
 
    useEffect(() => {
        if (typeof originalTags === 'object' && originalTags !== null) {
            setOriginalRequestFormValues({
                formValues: originalTags.details,
                isFormValid: false
            })
            reset(originalTags.details)
        }
        // eslint-disable-next-line
    }, [originalTags])
 
    if (!originalTags || isLoadingTags) return <LoadingSpinner />
 
    const saveUpdatedTags = () => {
        console.log('save Updated Tags')
        const updateTagRequest: UpdateTags = {
            tagsID: tagsId,
            requestorID: thisUserEmail,
            action: 'UPDATE_TAGS',
            status: 'PENDING_APPROVAL',
            details: formValues
        }
        updateTagsById(updateTagRequest, {
            onSuccess: () => {
                setNotification({
                    type: 'success',
                    statusIconAriaLabel: 'Success',
                    msg: 'Tags Status Updated Successfully'
                })
                setShowTagsNotification(true)
                setTimeout(() => {
                    setShowTagsNotification(false)
                }, 3000)
            },
            onError: () => {
                setNotification({
                    type: 'error',
                    statusIconAriaLabel: 'Error',
                    msg: 'Error saving Tags, Please try again!'
                })
                setShowTagsNotification(true)
                setTimeout(() => {
                    setShowTagsNotification(false)
                }, 3000)
            }
        })
    }
 
    return (
        <>
            <ColumnLayout columns={layout_col} variant="default">
                <FormSelect
                    label="Platform Name"
                    name="platformName"
                    control={control}
                    rules={{
                        required: 'Please Select Option'
                    }}
                    setFormValues={setFormValues}
                    options={PLATFORM_LIST}
                    placeholder="Select Platform "
                />
                <FormInput
                    name="tagProject"
                    label="Project"
                    type="text"
                    control={control}
                    rules={{
                        required: `Please enter valid value, Example: LUNA, JANUS, VIASH, UDG1:PH2`,
                        pattern: {
                            value: /^[A-Z0-9]+(?::(PH|FS|UC)[A-Z0-9]{2,})?(?:-(TR|CR|DR)[A-Z0-9]{2,})?$/g,
                            message:
                                'Please enter valid value, Example: LUNA, JANUS, VIASH, UDG1:PH2'
                        },
                        minLength: {
                            value: 3,
                            message:
                                'Minimum length should be 3. Example: LUNA, JANUS, VIASH, UDG1:PH2'
                        }
                    }}
                    setFormValues={setFormValues}
                    placeholder="Example: LUNA, JANUS, VIASH, UDG1:PH2"
                />
 
                {/* <FormSelect
                    label="Platform Repo Type"
                    name="platformRepo"
                    control={control}
                    rules={{
                        required: 'Please Select Option'
                    }}
                    setFormValues={setFormValues}
                    options={PLATFORM_REPO_LIST}
                    placeholder="Select Platform Repo Type"
                /> */}
                <FormInput
                    name="systemAlias"
                    label="System Alias"
                    type="text"
                    placeholder="Example: hipms, namax"
                    control={control}
                    rules={{
                        required: 'System alias should be 5 lowercase letters, Example: hipms',
                        pattern: {
                            value: /^[a-z]{5}$/,
                            message: 'System alias should be 5 lowercase letters, Example: hipms'
                        },
                        validate: validateSystemAlias
                    }}
                    setFormValues={setFormValues}
                />
                <FormSelect
                    label="Data Classification"
                    name="dataClassification"
                    control={control}
                    rules={{
                        required: 'Please Select Option'
                    }}
                    setFormValues={setFormValues}
                    options={DATA_CLASSIFICATION_LIST}
                    placeholder="Select Data Classification"
                />
                <FormSelect
                    label="Funding Source"
                    name="fundingSourceType"
                    control={control}
                    rules={{
                        required: 'Please Select Option'
                    }}
                    setFormValues={setFormValues}
                    options={COST_TYPE_LIST}
                    placeholder="Select Funding Source Type"
                />
                <br />
                <FormInput
                    name="WBSCode"
                    label="WBS Code"
                    type="text"
                    control={control}
                    rules={{
                        pattern: {
                            value: /^[A-Z0-9]{2,5}-[A-Z0-9]{1,15}-[0-9]{2}(?:-[A-Z0-9]{2,})?(?:-[A-Z0-9]{2,})?$/g,
                            message: 'Please Enter all in CAPS, Ex. ISG-ANODOT-23, EDO-ARROW-23, ISG-PROJECTA-23, EDO1-PROJECTA-24, EDO-PROJECTC-24-SW-01'
                        },
                        minLength: {
                            value: 3,
                            message: 'Minimum length should be 3'
                        }
                    }}
                    setFormValues={setFormValues}
                    placeholder="Example: ISG-ANODOT-23, EDO-ARROW-23, ISG-PROJECTA-23, EDO1-PROJECTA-24, EDO-PROJECTC-24-SW-01"
                    disabled={
                        formValues?.fundingSourceType &&
                        formValues.fundingSourceType.value === 'opex'
                    }
                />
                <FormInput
                    name="costCenter"
                    label="Cost Center"
                    type="text"
                    control={control}
                    rules={{
                        pattern: {
                            value: /^[A-Z0-9]{1,5}-[A-Z0-9]{1,3}$/g,
                            message: 'Please Enter all in CAPS, Ex. ISG12-M2, EDOM1-24, EGA-A12, ISGM1-H1'
                        },
                        minLength: {
                            value: 3,
                            message: 'Minimum length should be 3'
                        }
                    }}
                    placeholder="Example: ISG12-M2, EDOM1-24, EGA-A12, ISGM1-H1"
                    setFormValues={setFormValues}
                    disabled={
                        formValues?.fundingSourceType &&
                        formValues.fundingSourceType.value === 'capex'
                    }
                />
            </ColumnLayout>
            <br />
            {Object.keys(dirtyFields).length > 0 && (
                <>
                    <Header
                        variant="h3"
                        description="Existing Tags has been updated, Would you like to save updated Tags"
                    ></Header>
                    <Button
                        variant="primary"
                        onClick={saveUpdatedTags}
                        loading={isLoadingUpdateTags}
                        disabled={!isValid}
                    >
                        Save Tags{' '}
                    </Button>
                    <br />
                    {showTagsNotification && (
                        <>
                            <br />
                            <Alert
                                statusIconAriaLabel={notification.statusIconAriaLabel}
                                type={notification.type}
                            >
                                {notification.msg}
                            </Alert>
                        </>
                    )}
                </>
            )}
 
            <br />
 
            <ApproverOverview
                approvalStatus={originalTags['approvalStatus']}
                tagsID={tagsId}
                description="Tick checkBox to Provide approval"
                //isDisabled={page_context == 'PROJECT' ? true : false}
            />
        </>
    )
}
 
export default EditGenericTags